import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { parseISO, addDays, startOfDay, isAfter } from "date-fns";
import {
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    RadioInput,
    TextArea,
} from "@jhool-io/fe-components";
import {
    IGetClientNotes,
    ISessionNote,
    IEditSafetyPlanPayload,
} from "../../../../modules/notes/types/notes.types";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import { SAFETY_PLAN_NOTE_LABELS } from "../../../../utils/constants";

import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";

import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

interface EditSafetyPlanProps {
    noteDetails: ISessionNote;
}

export default function EditSafetyPlan({ noteDetails }: EditSafetyPlanProps) {
    const [localDateOfNextSession, setLocalDateOfNextSession] =
        React.useState<Date | null>(
            noteDetails?.note_content?.date_of_next_session
                ? parseISO(noteDetails?.note_content?.date_of_next_session)
                : null
        );
    const [localDateCreated, setLocalDateCreated] = React.useState<Date | null>(
        noteDetails?.note_content?.date_created
            ? parseISO(noteDetails?.note_content?.date_created)
            : null
    );
    const [showPrompt, setShowPrompt] = React.useState(false);
    const [selectedNote, setSelectedNote] = React.useState(
        {} as IGetClientNotes
    );
    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const isSigning = React.useRef(false);

    const { handleAutoSaveNote, handleEditDraftNote, handleSignNote } =
        useEditNote();

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const {
        register,
        handleSubmit,
        getValues,
        control,
        setValue,
        watch,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditSafetyPlanPayload>({
        resolver: yupResolver(
            yup.object({
                duration: yup.string(),
                diagnosis_codes: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .min(1, "Diagnosis is required")
                        .required("Diagnosis is required"),
                    otherwise: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .nullable(),
                }),
                secondary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                tertiary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_one: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_two: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                warning_signs: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Warning signs is required"),
                    otherwise: yup.string(),
                }),
                internal_coping_strategies: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Internal coping strategies is required"),
                    otherwise: yup.string(),
                }),
                people_and_social_settings_that_provide_distraction: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("Field is required"),
                        otherwise: yup.string(),
                    }),
                people_whom_i_can_ask_for_help: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Field is required"),
                    otherwise: yup.string(),
                }),
                professionals_or_agencies_i_can_contact_during_a_crisis: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("Field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                making_the_environment_safe: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Field is required"),
                    otherwise: yup.string().nullable(),
                }),
                the_one_thing_that_is_important_to_me_and_worth_living_for_is:
                    yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("Field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                how_likely_is_the_client_to_use_this_safety_plan: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                how_likely_is_the_client_to_adhere_to_this_safety_plan: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                detail_hloc_options_and_next_steps: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Field is required"),
                    otherwise: yup.string().nullable(),
                }),
                where_will_the_client_keep_the_safety_plan_and_how_will_it_be_located_during_a_crisis:
                    yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("Field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                is_the_format_appropriate_for_client_capacity_and_circumstances:
                    yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                when_should_follow_up_be_done_for_this_client: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("Field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                frequency_of_sessions: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                other_frequency_of_sessions: yup
                    .string()
                    .when(
                        "frequency_of_sessions",
                        (frequency_of_sessions, field) =>
                            frequency_of_sessions === "Other" &&
                            isSigning.current
                                ? field.required(
                                      "Other frequency of sessions is required"
                                  )
                                : field
                    ),
                date_of_next_session: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .date()
                        .required("Date of next session is required")
                        .test(
                            "is-after-dos",
                            "Date of next session must be after the current session date",
                            function validateDateAfterDOS(value) {
                                if (!value) return false;
                                if (!noteDetails.date_of_service) return true;
                                const dos = startOfDay(
                                    parseISO(noteDetails.date_of_service)
                                );
                                const nextSession = startOfDay(value);
                                return isAfter(nextSession, dos);
                            }
                        ),
                    otherwise: yup.date().nullable(),
                }),
                date_created: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup.date().required("Field is required"),
                    otherwise: yup.date().nullable(),
                }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
                  when_should_follow_up_be_done_for_this_client:
                      noteDetails?.note_content
                          .when_should_follow_up_be_done_for_this_client &&
                      !Number.isNaN(
                          new Date(
                              noteDetails?.note_content.when_should_follow_up_be_done_for_this_client
                          ).getTime()
                      )
                          ? ""
                          : noteDetails?.note_content
                                .when_should_follow_up_be_done_for_this_client,
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    // Function to handle item click and set the selected note content
    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue("secondary_diagnosis", [
            note.note_content.secondary_diagnosis,
        ]);
        setValue("tertiary_diagnosis", [note.note_content.tertiary_diagnosis]);
        setValue("additional_diagnosis_one", [
            note.note_content.additional_diagnosis_one,
        ]);
        setValue("additional_diagnosis_two", [
            note.note_content.additional_diagnosis_two,
        ]);
        setValue(
            "detail_hloc_options_and_next_steps",
            note.note_content.detail_hloc_options_and_next_steps
        );
        setValue(
            "how_likely_is_the_client_to_adhere_to_this_safety_plan",
            note.note_content
                .how_likely_is_the_client_to_adhere_to_this_safety_plan
        );
        setValue(
            "how_likely_is_the_client_to_use_this_safety_plan",
            note.note_content.how_likely_is_the_client_to_use_this_safety_plan
        );
        setValue(
            "internal_coping_strategies",
            note.note_content.internal_coping_strategies
        );
        setValue(
            "is_the_format_appropriate_for_client_capacity_and_circumstances",
            note.note_content
                .is_the_format_appropriate_for_client_capacity_and_circumstances
        );
        setValue(
            "making_the_environment_safe",
            note.note_content.making_the_environment_safe
        );
        setValue(
            "people_and_social_settings_that_provide_distraction",
            note.note_content
                .people_and_social_settings_that_provide_distraction
        );
        setValue(
            "people_whom_i_can_ask_for_help",
            note.note_content.people_whom_i_can_ask_for_help
        );
        setValue(
            "professionals_or_agencies_i_can_contact_during_a_crisis",
            note.note_content
                .professionals_or_agencies_i_can_contact_during_a_crisis
        );
        setValue(
            "the_one_thing_that_is_important_to_me_and_worth_living_for_is",
            note.note_content
                .the_one_thing_that_is_important_to_me_and_worth_living_for_is
        );
        setValue("warning_signs", note.note_content.warning_signs);

        setValue(
            "when_should_follow_up_be_done_for_this_client",
            note.note_content.when_should_follow_up_be_done_for_this_client &&
                !Number.isNaN(
                    new Date(
                        note.note_content.when_should_follow_up_be_done_for_this_client
                    ).getTime()
                )
                ? ""
                : note.note_content
                      .when_should_follow_up_be_done_for_this_client
        );
        setValue(
            "where_will_the_client_keep_the_safety_plan_and_how_will_it_be_located_during_a_crisis",
            note.note_content
                .where_will_the_client_keep_the_safety_plan_and_how_will_it_be_located_during_a_crisis
        );
        setValue(
            "frequency_of_sessions",
            note.note_content.frequency_of_sessions
        );
        setValue(
            "other_frequency_of_sessions",
            note.note_content.other_frequency_of_sessions
        );
        setValue("date_created", note.note_content.date_created);
        setShowPrompt(false);
    };

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <div>
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}
            <form
                aria-label="edit safety plan form"
                onSubmit={handleSubmit(handleSignNote)}
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <Controller
                        name="diagnosis_codes"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={SAFETY_PLAN_NOTE_LABELS.diagnosis_codes}
                                value={field.value}
                                onChange={field.onChange}
                                isRequired
                                errorText={
                                    errors.diagnosis_codes?.type === "typeError"
                                        ? "Diagnosis codes are required"
                                        : errors.diagnosis_codes?.message
                                }
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="secondary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    SAFETY_PLAN_NOTE_LABELS.secondary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.secondary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    SAFETY_PLAN_NOTE_LABELS.tertiary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.tertiary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_one"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    SAFETY_PLAN_NOTE_LABELS.additional_diagnosis_one
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_one?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_two"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    SAFETY_PLAN_NOTE_LABELS.additional_diagnosis_two
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_two?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <p className="note-label note-label-req">
                    {`1. ${SAFETY_PLAN_NOTE_LABELS.warning_signs}`}
                </p>
                <div className="fg">
                    <TextArea
                        {...register("warning_signs")}
                        hasError={!!errors.warning_signs}
                        errorText={errors.warning_signs?.message}
                        defaultValue={noteDetails.note_content?.warning_signs}
                    />
                </div>
                <p className="note-label note-label-req">
                    {`2. ${SAFETY_PLAN_NOTE_LABELS.internal_coping_strategies}`}
                </p>
                <div className="fg">
                    <TextArea
                        {...register("internal_coping_strategies")}
                        hasError={!!errors.internal_coping_strategies}
                        errorText={errors.internal_coping_strategies?.message}
                        defaultValue={
                            noteDetails.note_content?.internal_coping_strategies
                        }
                    />
                </div>
                <p className="note-label note-label-req">
                    {`3. ${SAFETY_PLAN_NOTE_LABELS.people_and_social_settings_that_provide_distraction}`}
                </p>
                <div className="fg">
                    <TextArea
                        {...register(
                            "people_and_social_settings_that_provide_distraction"
                        )}
                        hasError={
                            !!errors.people_and_social_settings_that_provide_distraction
                        }
                        errorText={
                            errors
                                .people_and_social_settings_that_provide_distraction
                                ?.message
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.people_and_social_settings_that_provide_distraction
                        }
                    />
                </div>
                <p className="note-label note-label-req">
                    {`4. ${SAFETY_PLAN_NOTE_LABELS.people_whom_i_can_ask_for_help}`}
                </p>
                <div className="fg">
                    <TextArea
                        {...register("people_whom_i_can_ask_for_help")}
                        hasError={!!errors.people_whom_i_can_ask_for_help}
                        errorText={
                            errors.people_whom_i_can_ask_for_help?.message
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.people_whom_i_can_ask_for_help
                        }
                    />
                </div>
                <p className="note-label note-label-req">
                    {`5. ${SAFETY_PLAN_NOTE_LABELS.professionals_or_agencies_i_can_contact_during_a_crisis}`}
                </p>
                <div className="fg">
                    <TextArea
                        {...register(
                            "professionals_or_agencies_i_can_contact_during_a_crisis"
                        )}
                        hasError={
                            !!errors.professionals_or_agencies_i_can_contact_during_a_crisis
                        }
                        errorText={
                            errors
                                .professionals_or_agencies_i_can_contact_during_a_crisis
                                ?.message
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.professionals_or_agencies_i_can_contact_during_a_crisis
                        }
                    />
                </div>
                <p className="note-label note-label-req">
                    {`6. ${SAFETY_PLAN_NOTE_LABELS.making_the_environment_safe}`}
                </p>
                <div className="fg">
                    <TextArea
                        {...register("making_the_environment_safe")}
                        hasError={!!errors.making_the_environment_safe}
                        errorText={errors.making_the_environment_safe?.message}
                        defaultValue={
                            noteDetails.note_content
                                ?.making_the_environment_safe
                        }
                    />
                </div>
                <p className="note-label note-label-req">
                    {
                        SAFETY_PLAN_NOTE_LABELS.the_one_thing_that_is_important_to_me_and_worth_living_for_is
                    }
                </p>
                <div className="fg">
                    <TextArea
                        {...register(
                            "the_one_thing_that_is_important_to_me_and_worth_living_for_is"
                        )}
                        hasError={
                            !!errors.the_one_thing_that_is_important_to_me_and_worth_living_for_is
                        }
                        errorText={
                            errors
                                .the_one_thing_that_is_important_to_me_and_worth_living_for_is
                                ?.message
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.the_one_thing_that_is_important_to_me_and_worth_living_for_is
                        }
                    />
                </div>
                <p className="note-label note-label-req">
                    {
                        SAFETY_PLAN_NOTE_LABELS.how_likely_is_the_client_to_use_this_safety_plan
                    }
                </p>
                <div className="fg fg-space-between three flex">
                    <RadioInput
                        {...register(
                            "how_likely_is_the_client_to_use_this_safety_plan"
                        )}
                        label="Very likely"
                        value="Very likely"
                        defaultChecked={
                            noteDetails.note_content
                                ?.how_likely_is_the_client_to_use_this_safety_plan ===
                            "Very likely"
                        }
                    />
                    <RadioInput
                        {...register(
                            "how_likely_is_the_client_to_use_this_safety_plan"
                        )}
                        label="Likely"
                        value="Likely"
                        defaultChecked={
                            noteDetails.note_content
                                ?.how_likely_is_the_client_to_use_this_safety_plan ===
                            "Likely"
                        }
                    />
                    <RadioInput
                        {...register(
                            "how_likely_is_the_client_to_use_this_safety_plan"
                        )}
                        label="Unlikely"
                        value="Unlikely"
                        defaultChecked={
                            noteDetails.note_content
                                ?.how_likely_is_the_client_to_use_this_safety_plan ===
                            "Unlikely"
                        }
                    />
                </div>
                {errors.how_likely_is_the_client_to_use_this_safety_plan ? (
                    <div className="note-error">
                        <p>Please select an option</p>
                    </div>
                ) : null}
                <p className="note-label note-label-req">
                    {
                        SAFETY_PLAN_NOTE_LABELS.how_likely_is_the_client_to_adhere_to_this_safety_plan
                    }
                </p>

                <div className="fg fg-space-between three flex">
                    <RadioInput
                        {...register(
                            "how_likely_is_the_client_to_adhere_to_this_safety_plan"
                        )}
                        label="Very likely"
                        value="Very likely"
                        defaultChecked={
                            noteDetails.note_content
                                ?.how_likely_is_the_client_to_adhere_to_this_safety_plan ===
                            "Very likely"
                        }
                    />
                    <RadioInput
                        {...register(
                            "how_likely_is_the_client_to_adhere_to_this_safety_plan"
                        )}
                        label="Likely"
                        value="Likely"
                        defaultChecked={
                            noteDetails.note_content
                                ?.how_likely_is_the_client_to_adhere_to_this_safety_plan ===
                            "Likely"
                        }
                    />
                    <RadioInput
                        {...register(
                            "how_likely_is_the_client_to_adhere_to_this_safety_plan"
                        )}
                        label="Unlikely"
                        value="Unlikely"
                        defaultChecked={
                            noteDetails.note_content
                                ?.how_likely_is_the_client_to_adhere_to_this_safety_plan ===
                            "Unlikely"
                        }
                    />
                </div>
                {errors.how_likely_is_the_client_to_adhere_to_this_safety_plan ? (
                    <div className="note-error">
                        <p>Please select an option</p>
                    </div>
                ) : null}
                <p className="note-label note-label-req">
                    {SAFETY_PLAN_NOTE_LABELS.detail_hloc_options_and_next_steps}
                </p>
                <div className="fg">
                    <TextArea
                        {...register("detail_hloc_options_and_next_steps")}
                        hasError={!!errors.detail_hloc_options_and_next_steps}
                        errorText={
                            errors.detail_hloc_options_and_next_steps?.message
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.detail_hloc_options_and_next_steps
                        }
                    />
                </div>
                <p className="note-label note-label-req">
                    {
                        SAFETY_PLAN_NOTE_LABELS.where_will_the_client_keep_the_safety_plan_and_how_will_it_be_located_during_a_crisis
                    }
                </p>
                <div className="fg">
                    <TextArea
                        {...register(
                            "where_will_the_client_keep_the_safety_plan_and_how_will_it_be_located_during_a_crisis"
                        )}
                        hasError={
                            !!errors.where_will_the_client_keep_the_safety_plan_and_how_will_it_be_located_during_a_crisis
                        }
                        errorText={
                            errors
                                .where_will_the_client_keep_the_safety_plan_and_how_will_it_be_located_during_a_crisis
                                ?.message
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.where_will_the_client_keep_the_safety_plan_and_how_will_it_be_located_during_a_crisis
                        }
                    />
                </div>
                <p className="note-label note-label-req">
                    {
                        SAFETY_PLAN_NOTE_LABELS.is_the_format_appropriate_for_client_capacity_and_circumstances
                    }
                </p>
                <div className="fg fg-space-between two flex">
                    <RadioInput
                        {...register(
                            "is_the_format_appropriate_for_client_capacity_and_circumstances"
                        )}
                        label="Yes"
                        value="Yes"
                        defaultChecked={
                            noteDetails.note_content
                                ?.is_the_format_appropriate_for_client_capacity_and_circumstances ===
                            "Yes"
                        }
                    />
                    <RadioInput
                        {...register(
                            "is_the_format_appropriate_for_client_capacity_and_circumstances"
                        )}
                        label="No"
                        value="No"
                        defaultChecked={
                            noteDetails.note_content
                                ?.is_the_format_appropriate_for_client_capacity_and_circumstances ===
                            "No"
                        }
                    />
                </div>
                {errors.is_the_format_appropriate_for_client_capacity_and_circumstances ? (
                    <div className="note-error">
                        <p>Please select an option</p>
                    </div>
                ) : null}
                <p className="note-label note-label-req">
                    {
                        SAFETY_PLAN_NOTE_LABELS.when_should_follow_up_be_done_for_this_client
                    }
                </p>
                <div className="fg">
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "when_should_follow_up_be_done_for_this_client"
                            )}
                            label="1 week"
                            value="1 week"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.when_should_follow_up_be_done_for_this_client ===
                                "1 week"
                            }
                        />
                        <RadioInput
                            {...register(
                                "when_should_follow_up_be_done_for_this_client"
                            )}
                            label="2 weeks"
                            value="2 weeks"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.when_should_follow_up_be_done_for_this_client ===
                                "2 weeks"
                            }
                        />
                        <RadioInput
                            {...register(
                                "when_should_follow_up_be_done_for_this_client"
                            )}
                            label="3 weeks"
                            value="3 weeks"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.when_should_follow_up_be_done_for_this_client ===
                                "3 weeks"
                            }
                        />
                        <RadioInput
                            {...register(
                                "when_should_follow_up_be_done_for_this_client"
                            )}
                            label="1 month"
                            value="1 month"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.when_should_follow_up_be_done_for_this_client ===
                                "1 month"
                            }
                        />
                    </div>
                    {errors.when_should_follow_up_be_done_for_this_client ? (
                        <div className="note-error">
                            <p>Please select an option</p>
                        </div>
                    ) : null}
                </div>
                <h3 className="note-label-req">
                    {SAFETY_PLAN_NOTE_LABELS.frequency_of_sessions}
                </h3>
                <div className="note-radios">
                    <RadioInput
                        {...register("frequency_of_sessions")}
                        label="Weekly"
                        value="Weekly"
                        defaultChecked={
                            noteDetails.note_content?.frequency_of_sessions ===
                            "Weekly"
                        }
                    />
                    <RadioInput
                        {...register("frequency_of_sessions")}
                        label="Bi-Weekly"
                        value="Bi-Weekly"
                        defaultChecked={
                            noteDetails.note_content?.frequency_of_sessions ===
                            "Bi-Weekly"
                        }
                    />
                    <RadioInput
                        {...register("frequency_of_sessions")}
                        label="Monthly"
                        value="Monthly"
                        defaultChecked={
                            noteDetails.note_content?.frequency_of_sessions ===
                            "Monthly"
                        }
                    />
                    <RadioInput
                        {...register("frequency_of_sessions")}
                        label="Other"
                        value="Other"
                        defaultChecked={
                            noteDetails.note_content?.frequency_of_sessions ===
                            "Other"
                        }
                    />
                </div>
                {errors.frequency_of_sessions ? (
                    <div className="note-error">
                        <p>Frequency of sessions is required</p>
                    </div>
                ) : null}
                {watch("frequency_of_sessions") === "Other" && (
                    <div className="fg">
                        <Input
                            {...register("other_frequency_of_sessions")}
                            label={
                                SAFETY_PLAN_NOTE_LABELS.other_frequency_of_sessions
                            }
                            placeholder={
                                SAFETY_PLAN_NOTE_LABELS.other_frequency_of_sessions
                            }
                            hasError={!!errors.other_frequency_of_sessions}
                            errorText={
                                errors.other_frequency_of_sessions?.message
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.other_frequency_of_sessions
                            }
                        />
                    </div>
                )}
                <div className="fg">
                    <Controller
                        name="date_of_next_session"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label={
                                    SAFETY_PLAN_NOTE_LABELS.date_of_next_session
                                }
                                onChange={(date) => {
                                    field.onChange(date);
                                    setLocalDateOfNextSession(date);
                                }}
                                selected={localDateOfNextSession}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                hasError={!!errors.date_of_next_session}
                                errorText={
                                    errors.date_of_next_session?.type ===
                                    "typeError"
                                        ? "Invalid date value"
                                        : errors.date_of_next_session?.message
                                }
                                shouldCloseOnSelect
                                isRequired
                                minDate={
                                    noteDetails.date_of_service
                                        ? addDays(
                                              parseISO(
                                                  noteDetails.date_of_service
                                              ),
                                              1
                                          )
                                        : undefined
                                }
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="date_created"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label={SAFETY_PLAN_NOTE_LABELS.date_created}
                                onChange={(date) => {
                                    field.onChange(date);
                                    setLocalDateCreated(date);
                                }}
                                selected={localDateCreated}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                shouldCloseOnSelect
                                hasError={!!errors.date_created}
                                errorText={
                                    errors.date_created?.type === "typeError"
                                        ? "Invalid date value"
                                        : errors.date_created?.message
                                }
                                isRequired
                            />
                        )}
                    />
                </div>
                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </div>
    );
}
